import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField
} from '@mui/material';
import {
  Assignment,
  Group,
  Schedule,
  BugReport,
  Add,
  Person,
  Delete,
  PlayArrow,
  Stop,
  Dashboard,
  CalendarToday
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import {
  Project,
  ProjectAssignment,
  Issue,
  User,
  UserRole,
  Sprint,
  Board
} from '../types';
import CreateIssueModal from '../components/Common/CreateIssueModal';
import IssueDetailsModal from '../components/Common/IssueDetailsModal';

const ProjectDetail: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageTeam = !!user && [
    UserRole.MANAGER, 
    UserRole.SUPER_MANAGER, 
    UserRole.ADMIN, 
    UserRole.ROOT_USER, 
    UserRole.HR_ADMIN,
    UserRole.PROJECT_MANAGER,
    UserRole.PRODUCT_OWNER,
    UserRole.SCRUM_MASTER
  ].includes(user.role);
  
  const [project, setProject] = useState<Project | null>(null);
  const [assignments, setAssignments] = useState<ProjectAssignment[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [openAssignmentModal, setOpenAssignmentModal] = useState(false);
  const [openCreateStoryModal, setOpenCreateStoryModal] = useState(false);
  const [openCreateTeamModal, setOpenCreateTeamModal] = useState(false);
  const [openCreateSprintModal, setOpenCreateSprintModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  
  // Assignment form data
  const [assignmentFormData, setAssignmentFormData] = useState({
    userId: '',
    role: ''
  });
  const [availableEmployees, setAvailableEmployees] = useState<User[]>([]);
  const [, setAssignmentLoading] = useState(false);
  
  // Team form data
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    description: '',
    capacity: 40
  });

  // Sprint form data
  const [sprintFormData, setSprintFormData] = useState({
    name: '',
    goal: '',
    startDate: '',
    endDate: '',
    capacity: 40
  });

  useEffect(() => {
    if (projectId) {
      loadProjectData();
    }
  }, [projectId]);

  const loadProjectData = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const [projectData, assignmentsData, teamsData, sprintsData, boardsData] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getProjectAssignments(projectId),
        apiService.getTeams(projectId),
        apiService.getProjectSprints(projectId, true), // Include completed sprints
        apiService.getBoardsForProject(projectId)
      ]);
      
      // Get issues for this project
      const issuesData = await apiService.getIssues({ projectId });
      
      setProject(projectData);
      setAssignments(assignmentsData);
      setTeams(teamsData);
      setSprints(sprintsData);
      setBoards(boardsData);
      setIssues(Array.isArray(issuesData.data) ? issuesData.data.flat() : []);
    } catch (err) {
      console.error('Error loading project data:', err);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableEmployees = async () => {
    try {
      console.log('🔍 Loading available employees...');
      const employees = await apiService.getAvailableEmployees();
      console.log('🔍 Employees loaded:', employees);
      setAvailableEmployees(employees);
      console.log('🔍 Available employees state updated');
    } catch (err) {
      console.error('❌ Error loading employees:', err);
    }
  };

  const handleAssignEmployees = () => {
    console.log('🔍 Opening assignment modal and loading employees...');
    setOpenAssignmentModal(true);
    loadAvailableEmployees();
  };

  const handleCloseAssignmentModal = () => {
    setOpenAssignmentModal(false);
    setAssignmentFormData({ userId: '', role: '' });
  };

  const handleCreateTeam = () => {
    setTeamFormData({
      name: '',
      description: '',
      capacity: 40
    });
    setOpenCreateTeamModal(true);
  };

  const handleCloseCreateTeamModal = () => {
    setOpenCreateTeamModal(false);
    setTeamFormData({ name: '', description: '', capacity: 40 });
  };

  const handleCreateTeamSubmit = async () => {
    if (!projectId) return;
    
    try {
      setAssignmentLoading(true);
      const teamData = {
        ...teamFormData,
        projectId
      };
      
      await apiService.createTeam(teamData);
      handleCloseCreateTeamModal();
      await loadProjectData(); // Reload to show the new team
    } catch (err) {
      console.error('Error creating team:', err);
      setError('Failed to create team');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!projectId || !assignmentFormData.userId || !assignmentFormData.role) return;
    
    try {
      setAssignmentLoading(true);
      await apiService.assignEmployeeToProject(assignmentFormData.userId, projectId, assignmentFormData.role);
      await loadProjectData();
      handleCloseAssignmentModal();
    } catch (err) {
      console.error('Error assigning employee:', err);
      setError('Failed to assign employee');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleCreateStory = async (issue: Issue) => {
    await loadProjectData();
    setOpenCreateStoryModal(false);
  };

  // Sprint management handlers
  const handleCreateSprint = () => {
    setOpenCreateSprintModal(true);
  };

  const handleCloseCreateSprintModal = () => {
    setOpenCreateSprintModal(false);
    setSprintFormData({
      name: '',
      goal: '',
      startDate: '',
      endDate: '',
      capacity: 40
    });
  };

  const handleCreateSprintSubmit = async () => {
    if (!projectId) return;
    
    try {
      await apiService.createSprint({
        name: sprintFormData.name,
        goal: sprintFormData.goal,
        startDate: sprintFormData.startDate,
        endDate: sprintFormData.endDate,
        capacity: sprintFormData.capacity,
        projectId: projectId
      });
      
      await loadProjectData();
      handleCloseCreateSprintModal();
    } catch (error) {
      console.error('Error creating sprint:', error);
    }
  };

  const handleStartSprint = async (sprintId: string) => {
    try {
      await apiService.startSprint(sprintId);
      await loadProjectData();
    } catch (error) {
      console.error('Error starting sprint:', error);
    }
  };

  const handleCompleteSprint = async (sprintId: string) => {
    try {
      await apiService.completeSprint(sprintId);
      await loadProjectData();
    } catch (error) {
      console.error('Error completing sprint:', error);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'manager': return 'primary';
      case 'lead': return 'secondary';
      case 'developer': return 'success';
      case 'designer': return 'info';
      case 'tester': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'done': return 'success';
      case 'in_progress': return 'primary';
      case 'todo': return 'default';
      case 'testing': return 'warning';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSprintStateColor = (state: string) => {
    switch (state.toLowerCase()) {
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'planning': return 'primary';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !project) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error || 'Project not found'}</Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Project Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {project.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {project.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={project.methodology || 'Agile'} color="primary" />
            <Chip label={project.isActive ? 'Active' : 'Inactive'} color={project.isActive ? 'success' : 'default'} />
            {project.key && <Chip label={`Key: ${project.key}`} variant="outlined" />}
          </Box>
        </Box>

        {/* Project Overview Content */}
        <Paper sx={{ width: '100%' }}>
          <Grid container spacing={3}>
            {/* Quick Stats */}
            <Grid container spacing={3} sx={{ mt: 2, mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: '#d0d0d0'
                  }
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <Group />
                    </Avatar>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {assignments.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Team Members
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: '#d0d0d0'
                  }
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <BugReport />
                    </Avatar>
                    <Typography variant="h4" color="success.main" gutterBottom>
                      {issues.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Issues
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: '#d0d0d0'
                  }
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Avatar sx={{ bgcolor: 'info.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <Assignment />
                    </Avatar>
                    <Typography variant="h4" color="info.main" gutterBottom>
                      {issues.filter(issue => issue.status === 'Done').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed Issues
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  background: '#ffffff',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    borderColor: '#d0d0d0'
                  }
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <Schedule />
                    </Avatar>
                    <Typography variant="h4" color="warning.main" gutterBottom>
                      {issues.filter(issue => issue.status === 'In Progress').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      In Progress
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Team Section */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Project Team</Typography>
                  {/* Show buttons to all users */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Group />}
                      onClick={handleCreateTeam}
                    >
                      Create Team
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleAssignEmployees}
                    >
                      Add Employee
                    </Button>
                  </Box>
                </Box>
                {assignments.length === 0 ? (
                  <Alert severity="info">
                    No team members assigned to this project. Click "Add Employee" to assign team members.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Assigned Date</TableCell>
                          {canManageTeam && <TableCell>Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignments.slice(0, 5).map((assignment) => (
                          <TableRow key={assignment.id}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                                  <Person />
                                </Avatar>
                                <Box>
                                  <Typography variant="body2">
                                    {assignment.user ? `${assignment.user.firstName} ${assignment.user.lastName}` : 'Unknown User'}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {assignment.user?.email || 'No email'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={assignment.role.replace('_', ' ')} 
                                color={getRoleColor(assignment.role) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(assignment.assignedAt).toLocaleDateString()}
                            </TableCell>
                            {canManageTeam && (
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  title="Remove from Project"
                                  onClick={async () => {
                                    try {
                                      await apiService.removeEmployeeFromProject(assignment.userId, projectId!);
                                      await loadProjectData();
                                    } catch (err) {
                                      console.error('Failed to remove from project', err);
                                    }
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Recent Issues Section */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Recent Issues</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setOpenCreateStoryModal(true)}
                  >
                    Create Issue
                  </Button>
                </Box>
                {issues.length === 0 ? (
                  <Alert severity="info">
                    No issues found for this project. Click "Create Issue" to create and manage issues.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Issue</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Assignee</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {issues.slice(0, 5).map((issue) => (
                          <TableRow key={issue.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {issue.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {issue.issueKey}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={issue.status} 
                                color={getStatusColor(issue.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={issue.priority} 
                                color={getPriorityColor(issue.priority) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {issue.assignee ? `${issue.assignee.firstName} ${issue.assignee.lastName}` : 'Unassigned'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Sprints Section */}
        <Paper sx={{ width: '100%', mt: 3 }}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Project Sprints</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateSprint}
              >
                Create Sprint
              </Button>
            </Box>
            {sprints.length === 0 ? (
              <Alert severity="info">
                No sprints found for this project. Click "Create Sprint" to create your first sprint.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sprint</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sprints.slice(0, 5).map((sprint) => (
                      <TableRow key={sprint.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {sprint.name}
                          </Typography>
                          {sprint.goal && (
                            <Typography variant="caption" color="textSecondary">
                              {sprint.goal}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={sprint.state} 
                            color={getSprintStateColor(sprint.state) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {sprint.state === 'planning' && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PlayArrow />}
                                onClick={() => handleStartSprint(sprint.id)}
                                color="success"
                              >
                                Start
                              </Button>
                            )}
                            {sprint.state === 'active' && canManageTeam && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Stop />}
                                onClick={() => handleCompleteSprint(sprint.id)}
                                color="warning"
                              >
                                Complete
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>

        {/* Boards Section */}
        <Paper sx={{ width: '100%', mt: 3 }}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Project Boards</Typography>
              <Button
                variant="outlined"
                startIcon={<Dashboard />}
                onClick={() => {
                  console.log('🔍 View All Boards clicked, boards.length:', boards.length);
                  if (boards.length === 1 && boards[0]) {
                    // If there's only one board, go directly to it
                    console.log('🔍 Navigating to single board:', boards[0].id);
                    navigate(`/board/${projectId}?boardId=${boards[0].id}`);
                  } else {
                    // Otherwise, go to boards listing
                    console.log('🔍 Navigating to boards listing');
                    navigate(`/boards`);
                  }
                }}
              >
                {boards.length === 1 ? 'Open Board' : 'View All Boards'}
              </Button>
            </Box>
            {boards.length === 0 ? (
              <Alert severity="info">
                No boards found for this project. Create a board to start managing your issues.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Board</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {boards.slice(0, 5).map((board) => (
                      <TableRow key={board.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {board.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={board.type} 
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              console.log('🔍 Opening board (NEW CODE):', projectId, board.id, new Date().toISOString());
                              navigate(`/board/${projectId}?boardId=${board.id}`);
                            }}
                            startIcon={<Dashboard />}
                          >
                            Open Board
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>

        {/* Assignment Modal */}
        <Dialog open={openAssignmentModal} onClose={handleCloseAssignmentModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            Assign Employee to Project
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={assignmentFormData.userId}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, userId: e.target.value })}
                  label="Employee"
                >
                  {availableEmployees.length === 0 ? (
                    <MenuItem value="" disabled>
                      No employees available
                    </MenuItem>
                  ) : (
                    availableEmployees.map((employee) => {
                      console.log('🔍 Rendering employee option:', employee);
                      return (
                        <MenuItem key={employee.id} value={employee.id}>
                          {`${employee.firstName} ${employee.lastName}`.trim() || employee.email}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={assignmentFormData.role}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="developer">Developer</MenuItem>
                  <MenuItem value="designer">Designer</MenuItem>
                  <MenuItem value="tester">Tester</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="analyst">Analyst</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignmentModal}>Cancel</Button>
            <Button onClick={handleAssignmentSubmit} variant="contained">
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Story/Issue Modal */}
        <CreateIssueModal
          open={openCreateStoryModal}
          onClose={() => setOpenCreateStoryModal(false)}
          projectId={projectId!}
          onCreated={handleCreateStory}
          availableUsers={assignments.map(a => a.user!).filter(Boolean)}
          epics={[]}
        />

        {/* Create Team Modal */}
        <Dialog open={openCreateTeamModal} onClose={handleCloseCreateTeamModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            Create New Team
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Team Name"
                value={teamFormData.name}
                onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={teamFormData.description}
                onChange={(e) => setTeamFormData({ ...teamFormData, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label="Capacity (Story Points per Sprint)"
                type="number"
                value={teamFormData.capacity}
                onChange={(e) => setTeamFormData({ ...teamFormData, capacity: parseInt(e.target.value) || 40 })}
                margin="normal"
                inputProps={{ min: 1, max: 200 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateTeamModal}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTeamSubmit} 
              variant="contained"
              disabled={!teamFormData.name.trim()}
            >
              Create Team
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Sprint Modal */}
        <Dialog open={openCreateSprintModal} onClose={handleCloseCreateSprintModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            Create New Sprint
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Sprint Name"
                value={sprintFormData.name}
                onChange={(e) => setSprintFormData({ ...sprintFormData, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Sprint Goal"
                value={sprintFormData.goal}
                onChange={(e) => setSprintFormData({ ...sprintFormData, goal: e.target.value })}
                margin="normal"
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={sprintFormData.startDate}
                onChange={(e) => setSprintFormData({ ...sprintFormData, startDate: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={sprintFormData.endDate}
                onChange={(e) => setSprintFormData({ ...sprintFormData, endDate: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                fullWidth
                label="Capacity (Story Points)"
                type="number"
                value={sprintFormData.capacity}
                onChange={(e) => setSprintFormData({ ...sprintFormData, capacity: parseInt(e.target.value) || 40 })}
                margin="normal"
                inputProps={{ min: 1, max: 200 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateSprintModal}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSprintSubmit} 
              variant="contained"
              disabled={!sprintFormData.name.trim() || !sprintFormData.startDate || !sprintFormData.endDate}
            >
              Create Sprint
            </Button>
          </DialogActions>
        </Dialog>

        {/* Issue Details Modal */}
        {selectedIssue && (
          <IssueDetailsModal
            issue={selectedIssue}
            open={!!selectedIssue}
            onClose={() => setSelectedIssue(null)}
            onUpdate={() => {
              loadProjectData();
              setSelectedIssue(null);
            }}
          />
        )}
      </Box>
    </Layout>
  );
};

export default ProjectDetail;